/**
 * Send Gift Flow - Recipient Selection
 * =====================================
 * Step 2: Choose who to send the gift to
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, Search, User, Check, Heart, MessageCircle } from 'lucide-react';
import { getFirstName } from '../utils/nameUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SendGiftRecipientPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const category = location.state?.category;
  const preSelectedRecipient = location.state?.preSelectedRecipient || location.state?.recipient;
  
  const [isDark, setIsDark] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecipient, setSelectedRecipient] = useState(preSelectedRecipient || null);

  useEffect(() => {
    const checkTheme = () => {
      const saved = localStorage.getItem('nearU_theme');
      setIsDark(saved !== 'light');
    };
    checkTheme();
    const interval = setInterval(checkTheme, 100);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!category) {
      navigate('/send-gift/select');
      return;
    }
    fetchConnections();
  }, [category]);

  const fetchConnections = async () => {
    try {
      const res = await fetch(`${API}/matches`);
      if (res.ok) {
        const data = await res.json();
        // Handle both array and object responses
        setConnections(Array.isArray(data) ? data : (data.matches || []));
      }
    } catch (error) {
      console.error('Error fetching connections:', error);
    } finally {
      setLoading(false);
    }
  };

  const colors = {
    background: isDark ? '#0D0D0D' : '#FDFCFA',
    cardBg: isDark ? 'rgba(26, 15, 10, 0.95)' : 'rgba(255, 255, 255, 0.98)',
    text: isDark ? '#E6D7C3' : '#1A1410',
    textMuted: isDark ? '#8A7355' : '#4A4035',
    border: isDark ? 'rgba(165, 124, 51, 0.2)' : 'rgba(165, 124, 51, 0.15)',
    accent: '#D4AF37',
    gold: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)'
  };

  const filteredConnections = connections.filter(conn => 
    conn.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleRecipientSelect = (recipient) => {
    setSelectedRecipient(recipient);
  };

  const handleContinue = () => {
    if (selectedRecipient) {
      navigate('/send-gift/amount', {
        state: {
          category,
          recipient: selectedRecipient
        }
      });
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      paddingBottom: '120px', 
      background: isDark 
        ? 'linear-gradient(180deg, #0D0D0D 0%, #1A0F0A 50%, #0D0D0D 100%)'
        : 'linear-gradient(180deg, #FAFAFA 0%, #F5F5F5 100%)'
    }}>
      {/* Header */}
      <div style={{ 
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        <button
          onClick={() => navigate('/send-gift/select')}
          style={{
            background: 'rgba(138, 115, 85, 0.15)',
            border: 'none',
            borderRadius: '10px',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: colors.text
          }}
        >
          <ChevronLeft size={22} />
        </button>
        
        <div style={{ flex: 1 }}>
          <h1 style={{ 
            fontFamily: 'Cinzel, serif', 
            fontSize: '22px',
            fontWeight: '600',
            color: colors.text,
            margin: 0
          }}>
            Send a Gift
          </h1>
          <p style={{ 
            color: colors.textMuted, 
            fontSize: '13px', 
            margin: '4px 0 0 0' 
          }}>
            Step 2 of 5 • Who is it for?
          </p>
        </div>
      </div>

      {/* Step Progress */}
      <div style={{ padding: '0 20px 24px' }}>
        <div style={{ 
          display: 'flex', 
          gap: '6px'
        }}>
          {[1, 2, 3, 4, 5].map((step) => (
            <div
              key={step}
              style={{
                flex: 1,
                height: '3px',
                borderRadius: '2px',
                background: step <= 2 ? colors.gold : 'rgba(138, 115, 85, 0.2)'
              }}
            />
          ))}
        </div>
      </div>

      {/* Selected Category Preview */}
      {category && (
        <div style={{ padding: '0 20px 20px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 16px',
            background: 'rgba(215, 181, 109, 0.08)',
            borderRadius: '12px',
            border: '1px solid rgba(215, 181, 109, 0.2)'
          }}>
            <span style={{ fontSize: '28px' }}>{category.emoji}</span>
            <div>
              <div style={{ color: colors.text, fontSize: '15px', fontWeight: '600' }}>
                {category.name}
              </div>
              <div style={{ color: colors.textMuted, fontSize: '12px' }}>
                {category.minAmount ? `$${(category.minAmount / 100).toLocaleString()}` : '$20'} – {category.maxAmount ? `$${(category.maxAmount / 100).toLocaleString()}` : '$5,000'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Title */}
      <div style={{ padding: '0 20px 20px', textAlign: 'center' }}>
        <h2 style={{
          fontFamily: 'Cinzel, serif',
          fontSize: '24px',
          color: colors.text,
          margin: '0 0 8px 0'
        }}>
          Who is it for?
        </h2>
        <p style={{
          color: colors.textMuted,
          fontSize: '14px',
          margin: 0
        }}>
          Select a connection
        </p>
      </div>

      {/* Search */}
      <div style={{ padding: '0 16px 20px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '14px 16px',
          background: colors.cardBg,
          border: `1px solid ${colors.border}`,
          borderRadius: '14px'
        }}>
          <Search size={20} color={colors.textMuted} />
          <input
            type="text"
            placeholder="Search connections..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              fontSize: '15px',
              color: colors.text
            }}
          />
        </div>
      </div>

      {/* Connections List */}
      <div style={{ padding: '0 16px' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: colors.textMuted }}>
            Loading connections...
          </div>
        ) : filteredConnections.length === 0 ? (
          <div style={{
            background: colors.cardBg,
            borderRadius: '16px',
            border: `1px solid ${colors.border}`,
            padding: '40px 24px',
            textAlign: 'center'
          }}>
            <User size={48} color={colors.textMuted} style={{ opacity: 0.4, marginBottom: '12px' }} />
            <p style={{ color: colors.textMuted, fontSize: '14px', margin: 0 }}>
              {searchQuery ? 'No connections found' : 'No connections yet'}
            </p>
          </div>
        ) : (
          <div style={{
            background: colors.cardBg,
            borderRadius: '16px',
            border: `1px solid ${colors.border}`,
            overflow: 'hidden'
          }}>
            {filteredConnections.map((conn, index) => {
              const isSelected = selectedRecipient?.id === conn.id;
              return (
                <div
                  key={conn.id}
                  onClick={() => handleRecipientSelect(conn)}
                  data-testid={`recipient-${conn.id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    padding: '16px',
                    cursor: 'pointer',
                    borderBottom: index < filteredConnections.length - 1 ? `1px solid ${colors.border}` : 'none',
                    background: isSelected ? 'rgba(215, 181, 109, 0.1)' : 'transparent',
                    transition: 'background 0.2s'
                  }}
                >
                  {/* Avatar */}
                  {conn.photo ? (
                    <img 
                      src={conn.photo} 
                      alt={conn.name}
                      style={{
                        width: '52px',
                        height: '52px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: isSelected ? '2px solid #D7B56D' : '2px solid transparent'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '52px',
                      height: '52px',
                      borderRadius: '50%',
                      background: 'rgba(215, 181, 109, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: isSelected ? '2px solid #D7B56D' : '2px solid transparent'
                    }}>
                      <User size={24} color={colors.accent} />
                    </div>
                  )}
                  
                  {/* Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      color: colors.text, 
                      fontSize: '16px', 
                      fontWeight: '600'
                    }}>
                      {getFirstName(conn.name)}
                    </div>
                    {conn.last_message && (
                      <div style={{ 
                        color: colors.textMuted, 
                        fontSize: '13px',
                        marginTop: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <MessageCircle size={12} />
                        {conn.last_message.substring(0, 30)}...
                      </div>
                    )}
                  </div>

                  {/* Selection Indicator */}
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    border: isSelected ? 'none' : `2px solid ${colors.border}`,
                    background: isSelected ? colors.gold : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {isSelected && <Check size={16} color="#2C1B13" strokeWidth={3} />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Continue Button */}
      <div style={{
        position: 'fixed',
        bottom: '80px',
        left: 0,
        right: 0,
        padding: '16px',
        background: isDark 
          ? 'linear-gradient(180deg, transparent 0%, #0D0D0D 30%)'
          : 'linear-gradient(180deg, transparent 0%, #FAFAFA 30%)'
      }}>
        <button
          onClick={handleContinue}
          disabled={!selectedRecipient}
          data-testid="continue-btn"
          style={{
            width: '100%',
            padding: '16px',
            background: selectedRecipient ? colors.gold : 'rgba(138, 115, 85, 0.3)',
            border: 'none',
            borderRadius: '14px',
            color: selectedRecipient ? '#2C1B13' : colors.textMuted,
            fontSize: '16px',
            fontWeight: '700',
            cursor: selectedRecipient ? 'pointer' : 'not-allowed',
            boxShadow: selectedRecipient ? '0 4px 20px rgba(215, 181, 109, 0.4)' : 'none'
          }}
        >
          {selectedRecipient ? `Continue with ${getFirstName(selectedRecipient.name)}` : 'Select a recipient'}
        </button>
      </div>
    </div>
  );
};

export default SendGiftRecipientPage;
